import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { useSettings } from '../../context/SettingsContext';
import api from '../../utils/api';
import './SimplePublicPage.css';

const IMG_BASE = process.env.REACT_APP_IMG_URL || 'http://localhost:5000';

function StaffCard({ m }) {
  return (
    <div className="staff-card">
      <div className="staff-photo">
        {m.photo
          ? <img src={`${IMG_BASE}${m.photo}`} alt={m.name}/>
          : <div className="staff-initials">{m.name.charAt(0)}</div>
        }
      </div>
      <p className="staff-name">{m.name}</p>
      {(m.title || m.position) && <p className="staff-title">{m.title || m.position}</p>}
      {(m.role_label) && <p className="staff-role">{m.role_label}</p>}
      {m.phone && <a href={`tel:${m.phone}`} className="staff-contact"><Phone size={11}/>{m.phone}</a>}
      {m.email && <a href={`mailto:${m.email}`} className="staff-contact"><Mail size={11}/>{m.email}</a>}
    </div>
  );
}

export default function SimplePublicPage({ title, apiPath, mode }) {
  const params  = useParams();
  const s       = useSettings();
  const [data,    setData]    = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);

  const resolved = apiPath.replace(':slug', params.slug || '');

  useEffect(() => {
    setLoading(true); setError(false);
    const calls = [api.get(resolved)];

    // For dept/service pages also load the related leadership
    if (mode === 'dept-detail' || mode === 'depts' || mode === 'services') {
      calls.push(api.get('/leadership').catch(() => ({ data: [] })));
    }

    Promise.all(calls)
      .then(([mainRes, leadRes]) => {
        setData(mainRes.data);
        if (leadRes) setLeaders(leadRes.data);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [resolved, mode]);

  const pageTitle = title || data?.title || '';

  const renderBody = () => {
    if (error)   return <div className="spp-error">Gabim: Të dhënat nuk u gjetën.</div>;
    if (loading) return <div className="spinner"/>;
    if (!data)   return null;

    // ── Static page
    if (mode === 'page') {
      return (
        <div className="spp-content rich-content"
          dangerouslySetInnerHTML={{ __html: data.content || '<p>Nuk ka përmbajtje.</p>' }}/>
      );
    }

    // ── Contact
    if (mode === 'contact') {
      return (
        <div className="contact-layout">
          <div className="contact-cards">
            <div className="contact-info-card card">
              <h2 className="contact-section-title">📞 Kontaktet</h2>
              <div className="contact-rows">
                {[
                  { icon: <Phone size={18}/>, label: 'Drejtoria',        val: data.main_phone,      href: `tel:${data.main_phone}`,       cls: '' },
                  { icon: <Phone size={18}/>, label: 'Emergjenca 24h',   val: data.emergency_phone, href: `tel:${data.emergency_phone}`,   cls: 'emg', iconCls: 'emg-icon', valCls: 'emg-val' },
                  { icon: <Phone size={18}/>, label: 'Linja falas',      val: data.free_line,       href: `tel:${data.free_line}`,         cls: 'free', iconCls: 'free-icon', valCls: 'free-val' },
                  { icon: <Mail size={18}/>,  label: 'Email',            val: data.email,            href: `mailto:${data.email}`,          cls: '' },
                  { icon: <MapPin size={18}/>,label: 'Adresa',           val: data.address,          href: null,                           cls: '' },
                  data.working_hours && { icon: <Clock size={18}/>, label: 'Orari', val: data.working_hours, href: null, cls: '' },
                ].filter(Boolean).map((row, i) => row.val && (
                  <div key={i} className="contact-row">
                    <div className={`contact-row-icon ${row.iconCls || ''}`}>{row.icon}</div>
                    <div>
                      <p className="contact-row-label">{row.label}</p>
                      {row.href
                        ? <a href={row.href} className={`contact-row-value ${row.valCls || ''}`}>{row.val}</a>
                        : <span className={`contact-row-value ${row.valCls || ''}`}>{row.val}</span>
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {data.map_embed && (
              <div className="contact-map card">
                <iframe src={data.map_embed} title="Harta" allowFullScreen loading="lazy"
                  style={{border:0,width:'100%',height:'100%',minHeight:320}}/>
              </div>
            )}
          </div>
        </div>
      );
    }

    // ── Departments list
    if (mode === 'depts' && Array.isArray(data)) {
      return (
        <div className="depts-list">
          {data.map((dept, i) => {
            // Staff from staff table
            const deptStaff = dept.staff || [];
            // Leaders registered under this department
            const deptLeaders = leaders.filter(
              l => l.category === 'departamenti' && String(l.department_id) === String(dept.id)
            );
            const allWorkers = [...deptStaff, ...deptLeaders];

            return (
              <div key={dept.id} className="dept-block card" style={{animationDelay:`${i*0.05}s`}}>
                <div className="dept-block-header">
                  {dept.image && <img src={`${IMG_BASE}${dept.image}`} alt={dept.name} className="dept-block-img"/>}
                  <div>
                    <h2 className="dept-block-name">{dept.name}</h2>
                    {dept.description && <p className="dept-block-desc">{dept.description}</p>}
                  </div>
                </div>
                {allWorkers.length > 0 && (
                  <>
                    <p className="staff-section-label">Stafi ({allWorkers.length} punonjës)</p>
                    <div className="staff-grid">
                      {allWorkers.map(m => <StaffCard key={`${m.id}-${m.name}`} m={m}/>)}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // ── Dept detail
    if (mode === 'dept-detail' && data.name) {
      const deptStaff   = data.staff || [];
      const deptLeaders = leaders.filter(
        l => l.category === 'departamenti' && String(l.department_id) === String(data.id)
      );
      const allWorkers = [...deptStaff, ...deptLeaders];
      return (
        <div>
          <div className="dept-detail-header card">
            {data.image && <img src={`${IMG_BASE}${data.image}`} alt={data.name} className="dept-detail-img"/>}
            <div className="dept-detail-info">
              <h1 className="dept-detail-name">{data.name}</h1>
              {data.description && <p className="dept-detail-desc">{data.description}</p>}
            </div>
          </div>
          {allWorkers.length > 0 && (
            <>
              <p className="staff-section-label">Stafi i departamentit ({allWorkers.length} punonjës)</p>
              <div className="staff-grid">
                {allWorkers.map(m => <StaffCard key={`${m.id}-${m.name}`} m={m}/>)}
              </div>
            </>
          )}
          {allWorkers.length === 0 && (
            <p style={{color:'var(--gray-400)',marginTop:'1.5rem'}}>Nuk ka staf të regjistruar për këtë departament.</p>
          )}
        </div>
      );
    }

    // ── Leadership
    if (mode === 'leadership' && Array.isArray(data)) {
      return (
        <div>
          {data.length === 0 && <p style={{color:'var(--gray-400)'}}>Nuk ka të dhëna.</p>}
          <div className="leadership-grid">
            {data.map(m => (
              <div key={m.id} className="leader-card card">
                <div className="leader-photo">
                  {m.photo
                    ? <img src={`${IMG_BASE}${m.photo}`} alt={m.name}/>
                    : <div className="leader-initials">{m.name.charAt(0)}</div>
                  }
                </div>
                <div className="leader-info">
                  <h3 className="leader-name">{m.name}</h3>
                  <p className="leader-pos">{m.position}</p>
                  {m.bio && <p className="leader-bio">{m.bio}</p>}
                  <div className="leader-contacts">
                    {m.email && <a href={`mailto:${m.email}`} className="leader-contact"><Mail size={13}/>{m.email}</a>}
                    {m.phone && <a href={`tel:${m.phone}`} className="leader-contact"><Phone size={13}/>{m.phone}</a>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // ── Services
    if (mode === 'services' && Array.isArray(data)) {
      return (
        <div>
          <div className="services-pub-grid">
            {data.map(svc => {
              const svcLeaders = leaders.filter(
                l => l.category === 'sherbimi' && String(l.service_id) === String(svc.id)
              );
              return (
                <div key={svc.id} className="service-pub-card card">
                  <div className="service-pub-icon">{svc.icon || '⚕️'}</div>
                  <h3 className="service-pub-name">{svc.name}</h3>
                  {svc.description && <p className="service-pub-desc">{svc.description}</p>}
                  {svcLeaders.length > 0 && (
                    <div className="service-staff">
                      <p className="service-staff-label">Stafi</p>
                      {svcLeaders.map(l => (
                        <div key={l.id} className="service-staff-row">
                          {l.photo
                            ? <img src={`${IMG_BASE}${l.photo}`} alt={l.name} className="service-staff-photo"/>
                            : <div className="service-staff-initials">{l.name.charAt(0)}</div>
                          }
                          <div>
                            <p className="service-staff-name">{l.name}</p>
                            <p className="service-staff-pos">{l.position}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {data.length === 0 && <p style={{color:'var(--gray-400)'}}>Nuk ka shërbime.</p>}
        </div>
      );
    }

    return <p style={{color:'var(--gray-400)'}}>Nuk ka të dhëna.</p>;
  };

  return (
    <>
      <Header />
      <div className="spp-hero">
        <div className="container">
          <h1 className="spp-title">{pageTitle}</h1>
        </div>
      </div>
      <div className="container spp-body">
        {renderBody()}
      </div>
      <Footer />
    </>
  );
}
